import { useEffect, useRef } from 'react';

// "Digital rain" of The Matrix Resurrections style: full-width katakana falling in three depth layers
// (far = small, dim and blurred; near = large, bright, glowing), a white-green head on every drop and
// a trail whose glyphs keep changing. Light on purpose: 24 fps, few drops per layer, no per-glyph
// shadow. Only in the dark theme; paused when the tab is hidden; a still frame when the user prefers
// reduced motion.

const GLYPH = 'アイウエオカキクケコサシスセソタチツテトナニヌネノハヒフヘホマミムメモヤユヨラリルレロワヲンァィゥェォッャュョ0123456789';
const FPS = 24;

interface Layer {
  font: number; // px
  column: number; // px between columns
  row: number; // px between rows
  speed: [number, number]; // rows per second
  fade: [number, number]; // trail brightness lost per second
  density: number; // share of columns with a drop
  alpha: number;
  filter: string; // css filter of the layer: blur for depth, glow for the near one
}

const LAYER: Layer[] = [
  { font: 12, column: 13, row: 13, speed: [5, 11], fade: [0.3, 0.6], density: 0.5, alpha: 0.5, filter: 'blur(1.2px)' },
  { font: 17, column: 19, row: 18, speed: [7, 14], fade: [0.3, 0.55], density: 0.38, alpha: 0.8, filter: 'blur(0.4px)' },
  { font: 24, column: 30, row: 25, speed: [8, 16], fade: [0.35, 0.6], density: 0.22, alpha: 1, filter: 'drop-shadow(0 0 6px rgba(60, 255, 110, 0.6))' },
];

const BUCKET = 10;
const TRAIL = Array.from({ length: BUCKET }, (_, index) => {
  const level = (index + 1) / BUCKET;
  return `rgba(${Math.round(70 * level * level)}, ${Math.round(150 + 105 * level)}, ${Math.round(60 + 50 * level)}, ${(0.05 + 0.95 * level * level).toFixed(3)})`;
});

const pick = () => GLYPH[Math.floor(Math.random() * GLYPH.length)]!;
const between = ([low, high]: [number, number]) => low + Math.random() * (high - low);

interface Drop {
  row: number;
  speed: number;
  fade: number;
}

interface Column {
  glyph: string[];
  light: number[];
  fade: number[];
  drop: Drop[];
}

interface Plane {
  layer: Layer;
  canvas: HTMLCanvasElement;
  context: CanvasRenderingContext2D;
  columns: Column[];
  rows: number;
}

const isDark = () => (document.documentElement.dataset.theme ?? 'dark') !== 'light';

export function MatrixRain() {
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const host = ref.current;
    if (!host) return;
    const reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    const planes: Plane[] = [];
    for (const layer of LAYER) {
      const canvas = document.createElement('canvas');
      const context = canvas.getContext('2d');
      if (!context) return;
      canvas.style.filter = layer.filter;
      host.appendChild(canvas);
      planes.push({ layer, canvas, context, columns: [], rows: 0 });
    }

    let width = 0;
    let height = 0;
    let ratio = 1;
    let frame = 0;
    let last = 0;
    let running = false;

    const newDrop = (plane: Plane, start: number): Drop => ({ row: start, speed: between(plane.layer.speed), fade: between(plane.layer.fade) });

    const resize = () => {
      ratio = Math.min(1.5, window.devicePixelRatio || 1);
      width = host.clientWidth;
      height = host.clientHeight;
      for (const plane of planes) {
        const { canvas, context, layer } = plane;
        canvas.width = Math.round(width * ratio);
        canvas.height = Math.round(height * ratio);
        context.setTransform(ratio, 0, 0, ratio, 0, 0);
        plane.rows = Math.ceil(height / layer.row) + 1;
        const count = Math.ceil(width / layer.column);
        plane.columns = Array.from({ length: count }, (_, index) => {
          const old = plane.columns[index];
          return {
            glyph: Array.from({ length: plane.rows }, (_, row) => old?.glyph[row] ?? pick()),
            light: Array.from({ length: plane.rows }, (_, row) => old?.light[row] ?? 0),
            fade: Array.from({ length: plane.rows }, (_, row) => old?.fade[row] ?? 0.5),
            drop: old?.drop ?? (Math.random() < layer.density ? [newDrop(plane, Math.random() * plane.rows * 1.3 - plane.rows * 0.3)] : []),
          };
        });
      }
    };

    const step = (seconds: number) => {
      for (const plane of planes) {
        const { rows, layer } = plane;
        for (const column of plane.columns) {
          for (let row = 0; row < rows; row++) {
            const light = column.light[row]!;
            if (light <= 0) continue;
            // Glyphs inside the trail keep mutating, as in the film.
            if (Math.random() < 0.7 * seconds) column.glyph[row] = pick();
            column.light[row] = Math.max(0, light - column.fade[row]! * seconds);
          }
          for (const drop of column.drop) {
            const from = Math.floor(drop.row);
            drop.row += drop.speed * seconds;
            for (let row = Math.max(0, from + 1); row <= Math.min(rows - 1, Math.floor(drop.row)); row++) {
              column.glyph[row] = pick();
              column.light[row] = 1;
              column.fade[row] = drop.fade;
            }
          }
          column.drop = column.drop.filter((drop) => drop.row < rows + 2);
          if (column.drop.length < 2 && Math.random() < layer.density * 0.6 * seconds) column.drop.push(newDrop(plane, -Math.random() * 6));
        }
      }
    };

    const draw = () => {
      for (const plane of planes) {
        const { context, layer, columns, rows } = plane;
        context.setTransform(ratio, 0, 0, ratio, 0, 0);
        context.clearRect(0, 0, width, height);
        context.globalAlpha = layer.alpha;
        context.font = `${layer.font}px 'MS Gothic', 'Yu Gothic', 'Hiragino Kaku Gothic ProN', 'Noto Sans JP', monospace`;
        context.textAlign = 'center';
        context.textBaseline = 'top';
        columns.forEach((column, index) => {
          const x = index * layer.column + layer.column / 2;
          for (let row = 0; row < rows; row++) {
            const light = column.light[row]!;
            if (light <= 0.03) continue;
            context.fillStyle = TRAIL[Math.min(BUCKET - 1, Math.floor(light * BUCKET))]!;
            context.fillText(column.glyph[row]!, x, row * layer.row);
          }
          context.fillStyle = '#eaffee';
          for (const drop of column.drop) {
            const row = Math.floor(drop.row);
            if (row >= 0 && row < rows) context.fillText(column.glyph[row]!, x, row * layer.row);
          }
        });
        context.globalAlpha = 1;
      }
    };

    const loop = (now: number) => {
      frame = requestAnimationFrame(loop);
      if (now - last < 1000 / FPS) return;
      const seconds = Math.min(0.1, (now - (last || now)) / 1000);
      last = now;
      step(seconds);
      draw();
    };

    const start = () => {
      if (running || !isDark() || document.hidden) return;
      running = true;
      host.style.visibility = 'visible';
      if (reduced) {
        draw();
        return;
      }
      last = 0;
      frame = requestAnimationFrame(loop);
    };

    const stop = () => {
      running = false;
      cancelAnimationFrame(frame);
    };

    const sync = () => {
      if (!isDark()) {
        stop();
        host.style.visibility = 'hidden';
      } else if (document.hidden) stop();
      else start();
    };

    resize();
    // Warm-up: the rain is already falling when the page opens, never an empty screen.
    for (let index = 0; index < 72; index++) step(1 / FPS);
    draw();
    sync();
    const size = new ResizeObserver(() => {
      resize();
      if (reduced && running) draw();
    });
    size.observe(host);
    const theme = new MutationObserver(sync);
    theme.observe(document.documentElement, { attributes: true, attributeFilter: ['data-theme'] });
    document.addEventListener('visibilitychange', sync);
    return () => {
      stop();
      size.disconnect();
      theme.disconnect();
      document.removeEventListener('visibilitychange', sync);
      for (const plane of planes) plane.canvas.remove();
    };
  }, []);

  return <div ref={ref} className="matrix-rain" aria-hidden="true" />;
}
